import React, { useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import { GRAY_COLORS } from '@/UIlibrary/globalTheme';
import {
	Maybe,
	MicrocreditRequest,
	MicrocreditRequestStatus,
	MicrocreditReviewMutation,
	useMicrocreditReviewMutation,
	ReviewMicrocreditOtherCreditsInput,
	ReviewMicrocreditCreditDetailsInput,
	ReviewMicrocreditCallCommentsInput,
	ReviewMicrocreditPersonalInformationInput,
	ReviewMicrocreditMonthlyHouseExpensesInput,
	ReviewMicrocreditMonthlyBusinessExpensesInput,
	ReviewMicrocreditMonthlyIncomeInformationInput,
	ReviewMicrocreditActivityOrBusinessInformationInput,
	OnboardingStatus
} from '@/graphql/__generated__/credit-requests';
import { RequestStatusBadge } from './RequestStatusBadge';
import ActivityOrBusinessInformation from './MicrocreditQuestionnaire/ActivityOrBusinessInformation';
import PersonalInformation from './MicrocreditQuestionnaire/PersonalInformation';
import CreditBureauReport from './MicrocreditQuestionnaire/CreditBureauReport';
import CreditDetails from './MicrocreditQuestionnaire/CreditDetails';
import MonthlyNetIncome from './MicrocreditQuestionnaire/MonthlyNetIncome';
import MonthlyBusinessExpenses from './MicrocreditQuestionnaire/MonthlyBusinessExpenses';
import MonthlyIncomeInformation from './MicrocreditQuestionnaire/MonthlyIncomeInformation';
import OtherCredits from './MicrocreditQuestionnaire/OtherCredits';
import ValueOfAssets from './MicrocreditQuestionnaire/ValueOfAssets';
import MonthlyHouseExpenses from './MicrocreditQuestionnaire/MonthlyHouseExpenses';
import Button from '@/UIlibrary/Button/Button';
import { Trash } from 'react-feather';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import toast from 'react-hot-toast';
import { useUpdateQuestionnaireStore } from '@/stores/useUpdateQuestionnaire';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import logger from '@/logger';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { Tooltip } from '@/UIlibrary/Tooltip/Tooltip';
import * as Sentry from '@sentry/react';

interface MicrocreditQuestionnaireProps {
	requestData: Maybe<MicrocreditRequest> | undefined;
	refetchData: () => Promise<any>;
}

const MicrocreditQuestionnaire: React.FC<MicrocreditQuestionnaireProps> = ({
	requestData,
	refetchData
}) => {
	const t = useTranslations('MicrocreditRequestQuestionnaire');
	const [isEditing, setIsEditing] = useState(false);
	const [formCallComments, setFormCallComments] = useState(requestData?.callComments || '');
	const [openConfirmChangesModal, setOpenConfirmChangesModal] = useState(false);
	const { getAllFormData, resetForm } = useUpdateQuestionnaireStore();
	const [formInputErrors, setFormInputErrors] = useState<Record<string, Record<string, string>>>(
		{}
	);
	const microcreditRequestsClient = useApolloClient(SCHEMAS.MICROCREDITS);

	useEffect(() => {
		if (requestData?.callComments) {
			setFormCallComments(requestData.callComments);
		}
	}, [requestData]);

	const extractMutationInvalidInputErrors = (
		mutationResult: MicrocreditReviewMutation
	): Record<string, Record<string, string>> => {
		const inputErrors: Record<string, Record<string, string>> = {};

		if (!mutationResult) {
			return inputErrors;
		}

		Object.entries(mutationResult).forEach(([sectionKey, sectionValue]) => {
			if (
				typeof sectionValue !== 'object' ||
				sectionValue === null ||
				!('mutationErrors' in sectionValue) ||
				!Array.isArray(sectionValue.mutationErrors)
			) {
				return;
			}
			const invalidInputErrors = sectionValue.mutationErrors.filter(
				(error: any) => error?.__typename === 'InvalidInput'
			);

			if (invalidInputErrors.length === 0) {
				return;
			}

			if (!inputErrors[sectionKey]) {
				inputErrors[sectionKey] = {};
			}

			invalidInputErrors.forEach((error: any) => {
				if (error.field && error.message) {
					inputErrors[sectionKey][error.field] = error.message;
				}
			});
		});
		toast.error(t('InputErrorUpdatingQuestionnaire'));
		return inputErrors;
	};

	const [microcreditReview, { loading }] = useMicrocreditReviewMutation({
		client: microcreditRequestsClient,
		onCompleted: async (data: MicrocreditReviewMutation) => {
			const allSectionWereSuccessfullyUpdated =
				data.reviewMicrocreditActivityOrBusinessInformation?.successful &&
				data.reviewMicrocreditCreditDetails?.successful &&
				data.reviewMicrocreditCallComments?.successful &&
				data.reviewMicrocreditMonthlyBusinessExpenses?.successful &&
				data.reviewMicrocreditMonthlyHouseExpenses?.successful &&
				data.reviewMicrocreditMonthlyIncomeInformation?.successful &&
				data.reviewMicrocreditOtherCredits?.successful &&
				data.reviewMicrocreditValueOfAssets?.successful &&
				data.reviewMicrocreditPersonalInformation?.successful;
			await refetchData();

			if (allSectionWereSuccessfullyUpdated) {
				logger.success('Questionnaire approved succesfully');
				toast.success(t('QuestionnaireUpdatedSuccessfully'));
				setIsEditing(false);
				setFormInputErrors({});
				resetForm();
				return;
			}
			Sentry.captureException(
				data.reviewMicrocreditActivityOrBusinessInformation?.mutationErrors ||
					data.reviewMicrocreditCreditDetails?.mutationErrors ||
					data.reviewMicrocreditCallComments?.mutationErrors ||
					data.reviewMicrocreditMonthlyBusinessExpenses?.mutationErrors ||
					data.reviewMicrocreditMonthlyHouseExpenses?.mutationErrors ||
					data.reviewMicrocreditMonthlyIncomeInformation?.mutationErrors ||
					data.reviewMicrocreditOtherCredits?.mutationErrors ||
					data.reviewMicrocreditValueOfAssets?.mutationErrors ||
					data.reviewMicrocreditPersonalInformation?.mutationErrors,
				{
					extra: {
						microcreditRequestId: requestData?.microcreditRequestId
					}
				}
			);
			const inputErrors = extractMutationInvalidInputErrors(data);
			setFormInputErrors(inputErrors);
			if (!inputErrors) {
				toast.error(t('ErrorUpdatingQuestionnaire'), { duration: 10000 });
			}
		},
		onError: (error) => {
			logger.error('Error updating microcredit questionnaire', error);
			toast.error(t('ErrorUpdatingQuestionnaire'));
		}
	});

	const handleConfirmSavedChanges = async () => {
		const getFormData = getAllFormData();

		logger.debug('CHANGES', getFormData);
		if (!formCallComments) {
			setFormInputErrors((prev) => ({
				...prev,
				reviewMicrocreditCallComments: {
					call_comments: t('CallCommentsRequired')
				}
			}));
			setOpenConfirmChangesModal(false);
			toast.error(t('CallCommentsRequired'));
			return;
		}
		await microcreditReview({
			variables: {
				callCommentsInput: {
					microcreditRequestId: requestData?.microcreditRequestId,
					callComments: formCallComments
				} as ReviewMicrocreditCallCommentsInput,
				personalInformationInput: {
					microcreditRequestId: requestData?.microcreditRequestId,
					...getFormData?.personalInfo,
					otherFamilyDependants: getFormData?.personalInfo?.otherFamilyDependants
						? Number(getFormData?.personalInfo?.otherFamilyDependants)
						: null,
					childDependants: getFormData?.personalInfo?.childDependants
						? Number(getFormData?.personalInfo?.childDependants)
						: null
				} as ReviewMicrocreditPersonalInformationInput,
				activityOrBusinessInformationInput: {
					microcreditRequestId: requestData?.microcreditRequestId,
					...getFormData?.activityOrBusiness
				} as ReviewMicrocreditActivityOrBusinessInformationInput,
				creditDetailsInput: {
					microcreditRequestId: requestData?.microcreditRequestId,
					...getFormData?.creditDetails
				} as ReviewMicrocreditCreditDetailsInput,
				monthlyBusinessExpensesInput: {
					microcreditRequestId: requestData?.microcreditRequestId,
					...getFormData?.monthlyBusinessExpenses
				} as ReviewMicrocreditMonthlyBusinessExpensesInput,
				monthlyHouseExpensesInput: {
					microcreditRequestId: requestData?.microcreditRequestId,
					...getFormData?.monthlyHouseExpenses
				} as ReviewMicrocreditMonthlyHouseExpensesInput,
				monthlyIncomeInformationInput: {
					microcreditRequestId: requestData?.microcreditRequestId,
					...getFormData?.monthlyIncome
				} as ReviewMicrocreditMonthlyIncomeInformationInput,
				otherCreditsInput: {
					microcreditRequestId: requestData?.microcreditRequestId,
					...getFormData?.otherCredits
				} as ReviewMicrocreditOtherCreditsInput,
				valueOfAssetsInput: {
					microcreditRequestId: requestData?.microcreditRequestId,
					...getFormData?.valueOfAssets
				} as ReviewMicrocreditOtherCreditsInput
			}
		});
		setOpenConfirmChangesModal(false);
	};

	if (!requestData) {
		return null;
	}

	if (loading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<div className="loader"></div>
			</div>
		);
	}

	const handleEditClick = () => {
		if (isEditing) {
			setOpenConfirmChangesModal(true);
		} else {
			setIsEditing(true);
		}
	};

	const handleDiscardChanges = () => {
		setIsEditing(false);
		resetForm();
		setFormCallComments(requestData?.callComments || '');
	};

	const canQuestionnaireBeEdited =
		requestData?.onboardingStatus !== OnboardingStatus.REJECTED &&
		requestData?.microcreditRequestStatus !==
			MicrocreditRequestStatus.REJECTED_BY_CREDIT_COMMITTEE &&
		requestData?.otherCredits !== null;

	const callCommentsHint = t('CallCommentsLength') + '. ' + formCallComments.length + '/3400';

	return (
		<div
			className="flex flex-col p-6 gap-5 rounded-lg mt-5 "
			style={{ backgroundColor: GRAY_COLORS.gray50 }}
		>
			<ConfirmModal
				isOpen={openConfirmChangesModal}
				title={t('SaveChanges')}
				onClose={() => setOpenConfirmChangesModal(false)}
				onCancel={() => setOpenConfirmChangesModal(false)}
				subtitle={t('SaveChangesMessage')}
				onConfirm={handleConfirmSavedChanges}
				confirmActionText={t('Save')}
			/>
			<div className="flex flex-row sticky top-0 z-10 bg-white p-4 shadow-md">
				<div className="flex flex-row items-center justify-between gap-4 w-full">
					{isEditing ? (
						<div className="flex flex-row items-center justify-start">
							<Typography weight="regular" color="subtitle" size="sm">
								*{t('QuestionnaireDisclaimer')}
							</Typography>
						</div>
					) : (
						<div className="flex grow" />
					)}
					<div className="flex flex-row items-center justify-end gap-4">
						{isEditing && (
							<div className="flex w-60">
								<Button
									label={t('DiscardChanges')}
									size="full"
									color="error"
									onClick={handleDiscardChanges}
									endIcon={<Trash />}
								/>
							</div>
						)}
						<div className="flex w-60">
							<Tooltip
								content={canQuestionnaireBeEdited ? '' : t('DisabledEditQuestionnaireTooltip')}
								position="bottom"
								id="edit-tooltip"
							>
								<Button
									label={isEditing ? t('SaveChanges') : t('EditQuestionnaire')}
									size="full"
									outlined={!isEditing}
									color={isEditing ? 'primary' : 'white'}
									onClick={handleEditClick}
									disabled={!canQuestionnaireBeEdited}
								/>
							</Tooltip>
						</div>
					</div>
				</div>
			</div>
			<div className="flex justify-end">
				{requestData?.microcreditRequestStatus && (
					<RequestStatusBadge status={requestData.microcreditRequestStatus} size="sm" />
				)}
			</div>
			<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
				<div className="grow">
					<Typography weight="semibold">{t('CallCommentsTitle')}</Typography>
				</div>
				<div className="w-full">
					<TextArea
						rows={4}
						value={formCallComments}
						onChange={(prev) => setFormCallComments(prev.target.value)}
						placeholder={t('CallCommentsPlaceholder')}
						disabled={!isEditing}
						error={!!formInputErrors?.reviewMicrocreditCallComments?.credit_area_opinion}
						hint={
							formInputErrors?.reviewMicrocreditCallComments?.credit_area_opinion ||
							callCommentsHint
						}
						maxChars={3400}
						strictMaxChars
					/>
				</div>
			</div>
			<MonthlyNetIncome data={requestData} title="MonthlyNetIncome" />
			<CreditDetails
				data={requestData.creditDetails}
				title="CreditDetails"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewMicrocreditCreditDetails}
			/>
			<CreditBureauReport data={requestData.creditBureauReport} title="CreditBureau" />
			<OtherCredits
				data={requestData.otherCredits}
				title="OtherCredits"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewMicrocreditOtherCredits}
			/>
			<PersonalInformation
				data={requestData.personalInformation}
				title="PersonalInformation"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewMicrocreditPersonalInformation}
			/>
			<ActivityOrBusinessInformation
				data={requestData.activityOrBusinessInformation}
				title="ActivityInformation"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewMicrocreditActivityOrBusinessInformation}
			/>
			<MonthlyIncomeInformation
				data={requestData.monthlyIncomeInformation}
				title="MonthlyIncomeInformation"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewMicrocreditMonthlyIncomeInformation}
			/>
			<MonthlyBusinessExpenses
				data={requestData.monthlyBusinessExpenses}
				title="MonthlyBusinessExpenses"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewMicrocreditMonthlyBusinessExpenses}
			/>
			<MonthlyHouseExpenses
				data={requestData.monthlyHouseExpenses}
				title="MonthlyHomeExpenses"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewMicrocreditMonthlyHouseExpenses}
			/>
			<ValueOfAssets
				data={requestData.valueOfAssets}
				title="ValueOfAssets"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewMicrocreditValueOfAssets}
			/>
		</div>
	);
};

export default MicrocreditQuestionnaire;
